import { useContext, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { validate } from '~/utils/validateAsset';
import { useFormik } from 'formik';
import styles from './EditAsset.module.scss';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { useToastError } from '~/utils/useToastError';
import { instance } from '~/utils/api/api';
import { useNavigate, useParams } from 'react-router-dom';
import EditAssetForm from './EditAssetForm/EditAssetForm';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { Button } from '~/ui';
import ModalConfirm from '~/components/Shared/ModalConfirm';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import cn from 'classnames';
import AssetPriceList from './AssetPriceList/AssetPriceList';
import { getPaginate } from '~/utils/getPaginate';
import { currencyData } from '~/utils/datas/currencyData';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';

export interface IColorScheme {
  id: number;
  name: string;
  color: string;
  text_color: string;
}

export interface IAssetValues {
  id: number;
  name: string;
  description: string;
  inn: string;
  color_id: number;
  logo?: File | null;
  address: string;
  image_path: string;
  delete_image: boolean;
  prefix: string;
  box_office: { avers_box_office_id: number; name: number; asset_id: number }[];
  mobile_sections: { id: number; name: string }[];
  admin_sections: { id: number; name: string }[];
  currency: { id: number; name: string; display_name: string };
}

const ACTIVE_TABS = {
  MAIN: 'main',
  PRICE_LIST: 'price_list',
};

const EditAsset = () => {
  const fileRef = useRef<HTMLInputElement>(null);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const [colors, setColors] = useState<IColorScheme[]>([]);
  const [asset, setAsset] = useState<IAssetValues>();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const client = useQueryClient();
  const [activeTab, setActiveTAb] = useState(ACTIVE_TABS.MAIN);
  const { paginate } = getPaginate();
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const { assetID: id } = useParams();

  const { mutateAsync: downloadFiles, isLoading: isDownloadFilesLoading } =
    useMutation(
      async (files: FileList) => {
        const formData = new FormData();
        Array.from(files).forEach(file => {
          formData.append('files[]', file);
        });
        formData.append('asset_id', String(id));
        const response = await instance.post('admin/upload-menu', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      },
      {
        onSuccess: () => {
          toastSuccess(t('files_successfully_uploaded'));
          client.invalidateQueries(['price-list', id]);
        },
        onError: error => {
          toastError(error);
        },
      }
    );

  const handleButtonClick = () => {
    fileRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      downloadFiles(e.target.files);
    } else {
      toastError(t('no_files_selected'));
    }
  };

  const { initialValues, storedInitialValues, clear, create } =
    useLocalStorage<IAssetValues>({
      initialValues: {
        id: 0,
        name: '',
        description: '',
        inn: '',
        color_id: 1,
        logo: null,
        address: '',
        image_path: '',
        delete_image: false,
        prefix: '',
        box_office: [],
        mobile_sections: [],
        admin_sections: [],
        currency: { id: 0, name: '', display_name: '' },
      },
      exclude: [],
    });

  const formik = useFormik({
    initialValues: storedInitialValues,

    validate,

    onSubmit: async values => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description);
      formData.append('inn', values.inn);
      if (values.logo && values.logo instanceof File) {
        formData.append('image', values.logo);
      }
      formData.append('color_id', String(values.color_id));
      formData.append('address', values.address);
      formData.append('currency', values.currency.name);
      const hasNewImage = values.logo && values.logo instanceof File;
      const shouldDeleteImage =
        values.delete_image || (!hasNewImage && !values.image_path);

      if (hasNewImage) {
        formData.append('delete_image', '0');
      } else {
        formData.append('delete_image', shouldDeleteImage ? '1' : '0');
      }

      if (values.prefix) {
        formData.append('prefix', values.prefix);
      }
      values.admin_sections.forEach(item => {
        formData.append('admin_section_ids[]', String(item.id));
      });
      values.mobile_sections.forEach(item => {
        formData.append('mobile_section_ids[]', String(item.id));
      });
      try {
        await instance.post(`admin/assets/${id}?_method=put`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        navigate(`/assets${paginate ? `?paginate=${paginate}` : ''}`);
        client.invalidateQueries(['assets']);
        clear();
        formik.resetForm({ values: initialValues });
        toastSuccess(t('asset_edited'));
      } catch (error) {
        toastError(error);
      } finally {
        setIsLoading(true);
      }
    },
  });

  create(formik.values);

  const loadColors = async () => {
    try {
      const { data } = await instance.get('admin/color-scheme');

      setColors(data);
    } catch (error) {
      toastError(error);
    }
  };

  const loadAssetById = async () => {
    setIsLoading(true);
    try {
      const { data } = await instance.get(`admin/assets/${id}`);

      const currentCurrency = await currencyData.find(
        item => item.name === data.currency
      );

      queryClient.setQueryData(['asset', id], data);
      formik.setValues({
        ...data,
        currency: currentCurrency,
      });

      const assetData = {
        ...data,
        delete_image: false,
        logo: null,
      };
      setAsset(assetData);
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAsset = async () => {
    try {
      await instance.delete(`admin/assets/${id}`);

      clear();
      formik.resetForm({ values: initialValues });
      client.invalidateQueries(['assets']);
      navigate(`/assets${paginate ? `?paginate=${paginate}` : ''}`);
      toastSuccess(t('asset_deleted'));
    } catch (error) {
      toastError(error);
    }
  };

  const updateData = () => {
    loadColors();
    loadAssetById();
  };

  useEffect(() => {
    updateData();
  }, []);

  return (
    <Page
      heading={
        <Heading
          text={asset?.name}
          classNameText={styles.createUserPageText}
          actions={
            <ModalConfirm
              title={`${t('asset_delete_confirm')} ${asset?.name}?`}
              onConfirm={() => deleteAsset()}
            >
              {open => (
                <Button
                  className={styles.deleteAssetButton}
                  disabled={false}
                  color="red"
                  text={t('delete')}
                  onClick={open}
                />
              )}
            </ModalConfirm>
          }
        />
      }
    >
      <Root
        value={activeTab}
        onValueChange={setActiveTAb}
        className={styles.roots}
      >
        <div className={styles.rootsHeader}>
          <List className={styles.rootsList}>
            <Trigger
              value={ACTIVE_TABS.MAIN}
              className={cn(styles.rootsTrigger, {
                [styles.activeTrigger]: activeTab === ACTIVE_TABS.MAIN,
              })}
            >
              {t('general_information')}
            </Trigger>
            <Trigger
              value={ACTIVE_TABS.PRICE_LIST}
              className={cn(styles.rootsTrigger, {
                [styles.activeTrigger]: activeTab === ACTIVE_TABS.PRICE_LIST,
              })}
            >
              {t('price_lists')}
            </Trigger>
          </List>
          {activeTab === ACTIVE_TABS.PRICE_LIST && isSuperAdmin && (
            <>
              <input
                disabled={isDownloadFilesLoading}
                type="file"
                multiple
                ref={fileRef}
                name="download file"
                placeholder="download file"
                onChange={handleChange}
                style={{ display: 'none' }}
                accept=".txt"
              />
              <Button
                text="Загрузить файлы"
                type="button"
                disabled={isDownloadFilesLoading}
                onClick={handleButtonClick}
                className={styles.downloadBtn}
              />
            </>
          )}
        </div>
        <Content value={ACTIVE_TABS.MAIN}>
          <EditAssetForm
            isLoading={isLoading}
            colorSchemes={colors}
            formik={formik}
            updateData={updateData}
          />
        </Content>
        <Content value={ACTIVE_TABS.PRICE_LIST}>
          <AssetPriceList assetId={id} />
        </Content>
      </Root>
    </Page>
  );
};

export default EditAsset;
